<script setup lang="ts">
/**
 * @author Alexander Kenchoshvili
 */
//@ts-nocheck
/** IMPORTS */
import { SecondaryDataInterface } from "@/types/ContentType";
import { computed, onBeforeMount, ref, watch, onMounted } from "vue";

import { useMainStore } from "@/store/main/index";
import { useBuildingStore } from "@/store/building";
import { LocationQuery, useRoute, useRouter } from "vue-router";
import type { BaseTaxonomy } from "@/store/main/types/widgets-terms/terms";

import { getTaxonomySS } from "@/composables/core/func";

import Button from "@/components/regularComponents/UIComponents/Button.vue";
import ProjectListBox from "./parts/ProjectListBox.vue";
import { Swiper, SwiperSlide } from "swiper/vue";
import { projectFilterSwiperOptions } from "./ts/projectFilter";
import "swiper/css";
// import Loading from "@/components/regularComponents/UIComponents/Loading/Loading.vue";

defineProps<{
  contentData: SecondaryDataInterface | undefined;
}>();

await getTaxonomySS("project_status");

const buildingStore = useBuildingStore();
const mainStore = useMainStore();
const activeType = ref<string>();

const router = useRouter();
const route = useRoute();

const delay: Ref<number> = ref(0.1);
const animateCards: Ref<boolean> = ref(false);

const fetchHandler = async (currentQuery: LocationQuery = route.query): Promise<void> => {
  activeType.value = (currentQuery?.type as string) || "all";

  const id = statuses.value?.find((el) => el.slug === activeType.value)?.id;
  const page = Number(route.query?.page) || 1;
  const taxonomy = id ? { project_status: [id] } : {};

  setTimeout(() => {
    buildingStore.getProjects({
      forceRefetch: true,
      params: {
        page,
        taxonomy,
        limit: 1000,
      },
    });
  }, 300);
};

const { t } = useI18n();
const indx = useState<IndxInterface>("data");

const getProjectLink = (projectDataItem: any): string => {
  if (!indx.value) return "";
  const matchedMenu = indx.value?.menus?.find(
    (menu) => menu.id === projectDataItem.relatedSitemap
  );
  if (!matchedMenu) return "";
  else return matchedMenu?.full_url;
};

const statuses = computed(() => {
  const fromStore = mainStore.terms?.project_status || [];
  const res: Partial<BaseTaxonomy>[] = [{ title: t("All"), slug: "all" }];

  res.push(...fromStore);

  return res;
});

const filterProjects = async (item: Partial<BaseTaxonomy>) => {
  router.push({
    query: {
      ...route.query,
      page: 1,
      type: item.slug!,
    },
  });
};

watch(
  () => route.query,
  async (newQuery) => {
    const isProjectsPage = route.params.path1 === "projects";
    if (!isProjectsPage) return;
    await fetchHandler(newQuery);
  }
);

onMounted(() => {
  setTimeout(() => {
    animateCards.value = true;
  }, 1000);
});

onBeforeMount(async () => {
  await fetchHandler();
});
</script>
<template>
  <div class="project-list">
    <div class="project-list__header d-flex j-btw al-center">
      <h1 class="project-list__title hl-1-plus uppercase">
        {{ contentData?.conf?.header?.title }}
      </h1>
      <ClientOnly>
        <div class="project-list__filter" v-if="statuses?.length">
          <div class="project-list__filter-wrapper d-flex">
            <Swiper
              :watchOverflow="true"
              :grab-cursor="true"
              slides-per-view="auto"
              :space-between="8"
              :breakpoints="projectFilterSwiperOptions.breakpoints"
            >
              <SwiperSlide v-for="(item, index) in statuses">
                <Button
                  :btn-type="8"
                  :key="index"
                  :btnText="item?.title"
                  :class="{ active: activeType === item.slug }"
                  @click="filterProjects(item)"
                />
              </SwiperSlide>
            </Swiper>
          </div>
        </div>
      </ClientOnly>
    </div>
    <div class="project-list__boxes" v-if="buildingStore.projectList?.length">
      <ProjectListBox
        v-for="(item, index) in buildingStore.projectList"
        :key="index"
        :data="item"
        :link="getProjectLink(item)"
        :delay="delay * (index + 1)"
        :animate="animateCards"
      />
    </div>
  </div>
  <!-- <Teleport to="body">
    <Loading :icon="false" />
  </Teleport> -->
</template>

<style lang="scss">
.project-list {
  padding-left: 160px;
  padding-right: 160px;
  padding-top: 76px;
  padding-bottom: 150px;
  @include maxq(desktop-2k) {
    padding-bottom: 120px;
  }
  @include maxq(desktop-lg) {
    padding-bottom: 85px;
  }
  @include maxq(tablet) {
    padding-top: 76px;
    padding-bottom: 85px;
    padding-left: 90px;
    padding-right: 90px;
  }
  @include maxq(mobile) {
    padding: 0px 48px;
    padding-top: 56px;
    padding-bottom: 60px;
  }
  @include maxq(mobile-sm) {
    padding: 0px 6px;
    padding-top: 32px;
    padding-bottom: 40px;
  }
  &__header {
    margin-bottom: 64px;
    padding: 0px 48px;
    h1 {
      @include weight(400);
      @include maxq(mobile-sm) {
        font-size: 40px;
      }
    }
    @include maxq(mobile) {
      flex-direction: column;
      align-items: flex-start;
      padding: 0;
    }
  }
  &__filter {
    @include maxq(mobile) {
      width: 100%;
      margin-top: 24px;
    }
    &-wrapper {
      background-color: $white;
      border: 1px solid $brightGray;
      max-width: fit-content;
      padding: 8px;
      .swiper {
        overflow: visible;
        width: 100%;
        .swiper-wrapper {
          width: fit-content;
          .swiper-slide {
            width: fit-content;
            &:last-child {
              margin-right: 0 !important;
            }
          }
        }
      }
      .button--8 {
        padding: 12px 16px;
        text-transform: capitalize;
      }
    }
  }

  &__boxes {
    display: grid;
    grid-template-columns: repeat(3, 31%);
    gap: 56px;

    @include maxq(tablet) {
      grid-template-columns: 50% 50%;
      gap: 40px;
    }

    @include maxq(mobile) {
      grid-template-columns: 1fr;
    }
  }
}
</style>
